import PageShell from '@/pages/shared/PageShell';

export default function AiFileManagement() {
  return (
    <PageShell
      title="AIファイル管理｜プロジェクト資料をAIが読み取り回答｜”おもいむすび”"
      description="プロジェクト資料をアップロードすると、AIが内容を読み取り質問に回答。参照ファイル名や該当箇所などの根拠情報を表示。社内資料のAI検索を実現します。"
      keywords="プロジェクト管理 ドキュメント管理,AI ファイル管理,社内資料 AI検索,ファイル 質問回答 AI,根拠付き AI回答"
      canonical="https://omoimusubi.asia/features/ai-file-management/"
      h1="プロジェクト資料をAIが読み、根拠付きで回答"
      subtitle="資料をアップロードすると、AIがファイルの内容を読み取り、ユーザーからの質問に対して根拠となるファイル名や該当箇所を明示して回答します。"
      relatedLinks={[
        { label: 'AIタスク分解', path: '/features/ai-task-breakdown/' },
        { label: 'プロジェクト文書管理ガイド', path: '/guides/project-document-management/' },
        { label: 'プロジェクト情報共有ガイド', path: '/guides/project-information-sharing/' },
      ]}
    >
      <div className="not-prose mb-8 rounded-xl overflow-hidden border border-background-200/70">
        <img
          src="https://readdy.ai/api/search-image?query=A%20Japanese%20male%20office%20worker%20in%20his%2030s%20sitting%20at%20a%20bright%20modern%20desk%20in%20a%20sunlit%20Japanese%20office%20pointing%20at%20his%20laptop%20screen%20with%20a%20satisfied%20smile%20the%20screen%20displays%20a%20document%20search%20interface%20showing%20AI%20generated%20answers%20with%20source%20file%20references%20in%20Japanese%20natural%20daylight%20through%20large%20windows%20clean%20white%20walls%20green%20plants%20on%20the%20desk%20refreshing%20professional%20contemporary%20Japanese%20workplace%20atmosphere&width=1200&height=700&seq=feature-ai-file-management-01&orientation=landscape&nocache=true"
          alt="AIファイル管理の製品画面 - 資料の内容をAIが読み取り根拠付きで回答"
          className="w-full h-auto"
        />
      </div>

      <div className="not-prose my-8 bg-background-100 rounded-lg border border-background-200/70 overflow-hidden">
        <div className="p-6 lg:p-8">
          <h2 className="text-base font-bold text-foreground-900 mb-4">AIファイル管理の利用イメージ</h2>
          <div className="bg-background-50 rounded-md p-4 border border-background-200/70 mb-4">
            <p className="text-xs text-foreground-500 mb-2">アップロード済み資料：企画書.pdf、要件定義書_v2.docx、議事録_0715.txt、見積書.xlsx</p>
            <div className="space-y-3">
              <div className="bg-white rounded p-3 border border-background-200">
                <div className="flex items-start gap-2 mb-2">
                  <div className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center shrink-0">
                    <span className="text-xs font-bold text-foreground-700">U</span>
                  </div>
                  <div className="bg-gray-100 rounded-lg px-3 py-2 text-xs text-foreground-800">このプロジェクトの納期はいつですか？</div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center shrink-0">
                    <span className="text-xs font-bold text-primary-700">AI</span>
                  </div>
                  <div className="bg-primary-50 rounded-lg px-3 py-2 text-xs text-foreground-800">
                    <p>プロジェクトの納期は<strong>2027年1月31日</strong>です。</p>
                    <div className="mt-2 pt-2 border-t border-primary-200/50">
                      <p className="text-[10px] text-foreground-500">📄 参照資料：</p>
                      <p className="text-[10px] text-primary-700">・企画書.pdf（3ページ「スケジュール」セクション）</p>
                      <p className="text-[10px] text-primary-700">・要件定義書_v2.docx（1ページ「プロジェクト概要」）</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <section>
        <h2>AIが答えられる質問の例</h2>
        <div className="not-prose grid grid-cols-1 sm:grid-cols-2 gap-3 my-4">
          {[
            { q: 'このプロジェクトの納期はいつですか', tag: 'スケジュール' },
            { q: 'クライアントからの追加要望は何ですか', tag: '要件' },
            { q: 'このタスクの完了条件は何ですか', tag: 'タスク' },
            { q: '最新の仕様変更は何ですか', tag: '仕様' },
            { q: '先週の議事録で決まったことは何ですか', tag: '議事録' },
            { q: '〇〇機能の見積もり金額はいくらですか', tag: '見積' },
            { q: 'このプロジェクトの体制を教えてください', tag: '体制' },
            { q: 'テストで見つかった不具合の一覧はありますか', tag: '品質' },
          ].map((item) => (
            <div key={item.q} className="bg-background-100 rounded-lg p-4 border border-background-200/70 flex items-start gap-3">
              <div className="w-8 h-8 bg-secondary-100 rounded-lg flex items-center justify-center shrink-0">
                <i className="ri-question-line text-secondary-600 text-sm"></i>
              </div>
              <div>
                <p className="text-sm text-foreground-800">{item.q}</p>
                <span className="text-[10px] text-foreground-500 mt-1 inline-block px-2 py-0.5 bg-background-50 rounded">{item.tag}</span>
              </div>
            </div>
          ))}
        </div>
        <p>各回答には、参照したファイル名や該当箇所などの根拠情報を表示します。回答の信頼性を確認できるため、重要な判断にも安心して活用できます。</p>
      </section>

      <section>
        <h2>ファイル管理の一元化</h2>
        <p>これまでファイルサーバー、メール、チャットツールなど複数の場所に分散していたプロジェクト資料を、”おもいむすび”上で一元管理できます。企画書、提案書、仕様書、議事録、進捗報告書、見積書、テスト仕様書など、あらゆるプロジェクト関連ファイルをアップロードしてAI検索の対象にできます。</p>
      </section>

      <section>
        <h2>対応ファイル形式</h2>
        <ul>
          <li>PDF（.pdf）</li>
          <li>Microsoft Word（.docx）</li>
          <li>Microsoft Excel（.xlsx）</li>
          <li>Microsoft PowerPoint（.pptx）</li>
          <li>テキストファイル（.txt, .csv）</li>
          <li>画像ファイル内のテキスト（OCR対応）</li>
        </ul>
        <p>1ファイルあたり最大50MBまでアップロード可能です。</p>
      </section>

      <section>
        <h2>よくある質問</h2>
        <h3>アップロードした資料の内容が外部に漏れることはありますか</h3>
        <p>お客様の資料データはテナントごとに完全に分離されて管理され、AIの追加学習に使われることはありません。通信はTLS 1.3で暗号化され、保存データも暗号化されています。</p>
        <h3>日本語以外の資料にも対応していますか</h3>
        <p>日本語、英語、タイ語の資料に対応しています。複数言語が混在した資料でも、AIが内容を理解して回答します。</p>
      </section>
    </PageShell>
  );
}